import { Button } from "./ui/button";
import { Menu, X, ChevronDown } from "lucide-react";
import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "@/lib/router-compat";
import { cn } from "@/lib/utils";
import { soluciones } from "@/lib/soluciones";
import {
  Globe,
  Star,
  MessageSquare,
  Inbox,
  Search,
  CalendarClock,
  Bot,
  Users,
  Workflow,
  LayoutGrid,
} from "lucide-react";

const iconMap = {
  globe: Globe,
  star: Star,
  message: MessageSquare,
  inbox: Inbox,
  search: Search,
  clock: CalendarClock,
  bot: Bot,
  users: Users,
  flow: Workflow,
  grid: LayoutGrid,
};

export const Navbar = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMegaOpen, setIsMegaOpen] = useState(false);
  const [isNosotrosOpen, setIsNosotrosOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const navLinks = [
    { name: "Precios", href: "/precios" },
    { name: "Testimonios", href: "/testimonios" },
    { name: "Trabajos", href: "/trabajos" },
  ];

  const nosotrosLinks = [
    {
      title: "Quiénes somos",
      tagline: "El equipo y cómo trabajamos",
      href: "/nosotros",
      icon: "users",
    },
    {
      title: "Nuestro proceso",
      tagline: "De la llamada inicial a la entrega en 7 a 10 días",
      href: "/#proceso",
      icon: "flow",
    },
    {
      title: "Rubros con los que trabajamos",
      tagline: "Empresas de servicios y distribuidores",
      href: "/#para-quien-es",
      icon: "grid",
    },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isMobileMenuOpen]);

  const handleNavigation = (
    e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
    href: string,
  ) => {
    e.preventDefault();

    const parts = href.split("#");
    const path = parts[0];
    const hash = parts[1];

    if (path && path !== location.pathname) {
      navigate(href);
      setIsMobileMenuOpen(false);

      if (hash) {
        setTimeout(() => {
          const element = document.getElementById(hash);
          if (element) {
            element.scrollIntoView({ behavior: "smooth" });
          }
        }, 150);
      } else {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
      return;
    }

    if (hash) {
      const element = document.getElementById(hash);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <nav
      className={cn(
        "fixed top-0 left-0 right-0 z-50 transition-all duration-300 ease-in-out border-b border-white/10",
        isScrolled || isMobileMenuOpen || isMegaOpen || isNosotrosOpen
          ? "bg-[#08080A]/70 backdrop-blur-md"
          : "bg-transparent",
      )}
    >
      <div className="container mx-auto px-6 max-w-[1200px]">
        <div className="flex items-center justify-between h-16">
          <div className="flex-shrink-0 relative z-50">
            <a
              href="/"
              onClick={(e) => handleNavigation(e, "/")}
              className="flex items-center gap-2"
            >
              <span className="text-[#FAFAFA] font-bold text-xl tracking-tight">
                crecelocal.
              </span>
            </a>
          </div>

          <div className="hidden md:block">
            <div className="ml-10 flex items-center space-x-8">
              {/* Mega menu trigger */}
              <div
                className="relative"
                onMouseEnter={() => setIsMegaOpen(true)}
                onMouseLeave={() => setIsMegaOpen(false)}
              >
                <button
                  onClick={() => setIsMegaOpen(!isMegaOpen)}
                  className="flex items-center gap-1 transition-colors text-[16px] font-medium text-[#A1A1AA] hover:text-[#FAFAFA]"
                >
                  Qué hacemos
                  <ChevronDown
                    className={cn(
                      "w-4 h-4 transition-transform",
                      isMegaOpen && "rotate-180",
                    )}
                  />
                </button>

                {isMegaOpen && (
                  <div className="absolute top-full left-1/2 -translate-x-1/2 pt-3 w-[640px]">
                    <div className="bg-[#0F0F11] border border-white/10 rounded-[16px] p-4 shadow-2xl">
                      <div className="grid grid-cols-2 gap-1">
                        {soluciones.map((sol) => {
                          const Icon = iconMap[sol.icon];
                          return (
                            <button
                              key={sol.slug}
                              onClick={() => {
                                navigate(`/que-hacemos/${sol.slug}`);
                                setIsMegaOpen(false);
                              }}
                              className="flex items-start gap-3 p-3 rounded-[10px] hover:bg-white/5 transition-colors text-left"
                            >
                              <div className="w-9 h-9 bg-white/5 border border-white/10 rounded-[8px] flex items-center justify-center shrink-0 mt-0.5">
                                <Icon className="w-4 h-4 text-[#7C3AED]" />
                              </div>
                              <div>
                                <p className="text-[#FAFAFA] text-[14px] font-medium mb-0.5">
                                  {sol.shortTitle}
                                </p>
                                <p className="text-[#A1A1AA] text-[12px] leading-[1.4]">
                                  {sol.tagline}
                                </p>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                      <div className="border-t border-white/10 mt-2 pt-3 px-3">
                        <button
                          onClick={() => {
                            navigate("/soluciones");
                            setIsMegaOpen(false);
                          }}
                          className="text-[#7C3AED] text-[13px] font-mono uppercase tracking-wider hover:text-[#A78BFA] transition-colors"
                        >
                          Ver todas las soluciones →
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Nosotros mega menu */}
              <div
                className="relative"
                onMouseEnter={() => setIsNosotrosOpen(true)}
                onMouseLeave={() => setIsNosotrosOpen(false)}
              >
                <button
                  onClick={() => setIsNosotrosOpen(!isNosotrosOpen)}
                  className="flex items-center gap-1 transition-colors text-[16px] font-medium text-[#A1A1AA] hover:text-[#FAFAFA]"
                >
                  Nosotros
                  <ChevronDown
                    className={cn(
                      "w-4 h-4 transition-transform",
                      isNosotrosOpen && "rotate-180",
                    )}
                  />
                </button>

                {isNosotrosOpen && (
                  <div className="absolute top-full left-1/2 -translate-x-1/2 pt-3 w-[480px]">
                    <div className="bg-[#0F0F11] border border-white/10 rounded-[16px] p-4 shadow-2xl">
                      <div className="grid grid-cols-1 gap-1">
                        {nosotrosLinks.map((item) => {
                          const Icon =
                            iconMap[item.icon as keyof typeof iconMap];
                          return (
                            <button
                              key={item.title}
                              onClick={(e) => {
                                e.preventDefault();
                                handleNavigation(
                                  e as unknown as React.MouseEvent<
                                    HTMLAnchorElement,
                                    MouseEvent
                                  >,
                                  item.href,
                                );
                                setIsNosotrosOpen(false);
                              }}
                              className="flex items-start gap-3 p-3 rounded-[10px] hover:bg-white/5 transition-colors text-left"
                            >
                              <div className="w-9 h-9 bg-white/5 border border-white/10 rounded-[8px] flex items-center justify-center shrink-0 mt-0.5">
                                <Icon className="w-4 h-4 text-[#7C3AED]" />
                              </div>
                              <div>
                                <p className="text-[#FAFAFA] text-[14px] font-medium mb-0.5">
                                  {item.title}
                                </p>
                                <p className="text-[#A1A1AA] text-[12px] leading-[1.4]">
                                  {item.tagline}
                                </p>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {navLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.href}
                  onClick={(e) => handleNavigation(e, link.href)}
                  className="transition-colors text-[16px] font-medium text-[#A1A1AA] hover:text-[#FAFAFA]"
                >
                  {link.name}
                </a>
              ))}
              <a
                href="https://app.crecelocal.com"
                target="_blank"
                rel="noopener noreferrer"
                className="transition-colors text-[16px] font-medium text-[#A1A1AA] hover:text-[#FAFAFA]"
              >
                Iniciar sesión
              </a>
              <Button
                onClick={() => {
                  import("@/lib/analytics").then(({ trackCTA }) =>
                    trackCTA("Navbar - Agendar llamada"),
                  );
                  navigate("/demo");
                }}
                className="bg-primary hover:bg-primary/90 hover:-translate-y-[2px] transition-all hover:shadow-[0_12px_32px_rgba(167,139,250,0.25)] text-primary-foreground font-medium rounded-xl px-6 py-[14px] h-auto"
              >
                Agendar llamada
              </Button>
            </div>
          </div>

          <div className="md:hidden relative z-50">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="inline-flex items-center justify-center p-2 rounded-md text-[#FAFAFA] hover:text-white/80 focus:outline-none transition-colors"
            >
              <span className="sr-only">Open main menu</span>
              {isMobileMenuOpen ? (
                <X className="block h-6 w-6" aria-hidden="true" />
              ) : (
                <Menu className="block h-6 w-6" aria-hidden="true" />
              )}
            </button>
          </div>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className="md:hidden fixed inset-0 z-40 flex flex-col pt-24 px-6 bg-[#08080A]/95 backdrop-blur-xl h-screen overflow-y-auto">
          <div className="relative z-10 flex flex-col space-y-6 mt-8">
            <button
              onClick={() => setIsMegaOpen(!isMegaOpen)}
              className="flex items-center justify-center gap-2 text-2xl font-medium transition-colors text-center text-[#A1A1AA] hover:text-[#FAFAFA] w-full"
            >
              <span>Qué hacemos</span>
              <ChevronDown
                className={cn(
                  "w-5 h-5 transition-transform",
                  isMegaOpen && "rotate-180",
                )}
              />
            </button>
            {isMegaOpen && (
              <div className="flex flex-col space-y-4 pl-2 border-l border-white/10">
                {soluciones.map((sol) => {
                  const Icon = iconMap[sol.icon];
                  return (
                    <button
                      key={sol.slug}
                      onClick={() => {
                        navigate(`/que-hacemos/${sol.slug}`);
                        setIsMobileMenuOpen(false);
                      }}
                      className="flex items-center gap-3 text-left"
                    >
                      <Icon className="w-5 h-5 text-[#7C3AED] shrink-0" />
                      <span className="text-[18px] font-medium text-[#A1A1AA] hover:text-[#FAFAFA]">
                        {sol.shortTitle}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
            {/* Nosotros mobile accordion */}
            <button
              onClick={() => setIsNosotrosOpen(!isNosotrosOpen)}
              className="flex items-center justify-center gap-2 text-2xl font-medium transition-colors text-center text-[#A1A1AA] hover:text-[#FAFAFA] w-full"
            >
              <span>Nosotros</span>
              <ChevronDown
                className={cn(
                  "w-5 h-5 transition-transform",
                  isNosotrosOpen && "rotate-180",
                )}
              />
            </button>
            {isNosotrosOpen && (
              <div className="flex flex-col space-y-4 pl-2 border-l border-white/10">
                {nosotrosLinks.map((item) => {
                  const Icon = iconMap[item.icon as keyof typeof iconMap];
                  return (
                    <button
                      key={item.title}
                      onClick={(e) => {
                        e.preventDefault();
                        handleNavigation(
                          e as unknown as React.MouseEvent<
                            HTMLAnchorElement,
                            MouseEvent
                          >,
                          item.href,
                        );
                        setIsMobileMenuOpen(false);
                      }}
                      className="flex items-center gap-3 text-left"
                    >
                      <Icon className="w-5 h-5 text-[#7C3AED] shrink-0" />
                      <span className="text-[18px] font-medium text-[#A1A1AA] hover:text-[#FAFAFA]">
                        {item.title}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                onClick={(e) => handleNavigation(e, link.href)}
                className="block text-2xl font-medium transition-colors text-center text-[#A1A1AA] hover:text-[#FAFAFA]"
              >
                {link.name}
              </a>
            ))}
            <a
              href="https://app.crecelocal.com"
              target="_blank"
              rel="noopener noreferrer"
              className="block text-2xl font-medium transition-colors text-center text-[#A1A1AA] hover:text-[#FAFAFA]"
            >
              Iniciar sesión
            </a>
            <div className="pt-4 flex justify-center">
              <Button
                onClick={() => {
                  import("@/lib/analytics").then(({ trackCTA }) =>
                    trackCTA("Navbar Mobile - Agendar llamada"),
                  );
                  navigate("/demo");
                  setIsMobileMenuOpen(false);
                }}
                className="bg-primary text-primary-foreground hover:bg-primary/90 font-medium text-lg px-8 py-6 rounded-xl w-full max-w-xs"
              >
                Agendar llamada
              </Button>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};
